/**
 * React-Query hooks for the EMS API.
 *
 * All hooks derive the query key from the request path + query params so
 * keys stay unique and easy to invalidate. We keep `staleTime` conservative
 * (60s) to avoid re-fetching while the user clicks through the navigation.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useMemo } from "react";

import { useAuth } from "@/auth/AuthProvider";
import { createApiClient, type ApiClient, type FetchOptions } from "./client";
import type {
  AdminDevice,
  AdminDeviceCreateBody,
  AdminDeviceListResponse,
  AdminDeviceUpdateBody,
  AdminAlarmRule,
  AdminAlarmRuleCreateBody,
  AdminAlarmRuleExportResponse,
  AdminAlarmRuleListResponse,
  AdminAlarmRuleUpdateBody,
  AdminMeter,
  AdminMeterCreateBody,
  AdminMeterListResponse,
  AdminMeterUpdateBody,
  AdminNotificationSettings,
  AdminNotificationTestResult,
  AdminNotificationSettingsUpdateBody,
  AggregateInterval,
  AggregateResponse,
  Alarm,
  AlarmListResponse,
  AlarmRuleStatus,
  AlarmSeverity,
  AlarmState,
  EvChargerCommand,
  EvChargerCommandCreateBody,
  EvChargerCommandListResponse,
  EvChargerCommandState,
  EvChargerCommandTransitionBody,
  EvDcModbusProfile,
  EvDcModbusTarget,
  EvDcModbusTargetCreateBody,
  EvDcModbusTargetListResponse,
  EvDcModbusTargetPollerExportResponse,
  EvDcModbusTargetUpdateBody,
  MeterListResponse,
  ReadingListResponse,
  SiteDevice,
  SiteDeviceListResponse,
  SiteListResponse,
  Whoami,
} from "./types";

function useApi(): ApiClient {
  const { getToken } = useAuth();
  return useMemo(() => createApiClient(getToken), [getToken]);
}

type Key = readonly [string, FetchOptions["query"]?];

function key(path: string, query?: FetchOptions["query"]): Key {
  return [path, query] as const;
}

export function useWhoami(): UseQueryResult<Whoami> {
  const api = useApi();
  return useQuery({
    queryKey: key("/v1/whoami"),
    queryFn: ({ signal }) => api.request<Whoami>("/v1/whoami", { signal }),
    staleTime: 5 * 60_000,
  });
}

export type SiteListParams = { limit?: number; offset?: number; search?: string };

export function useSites(params: SiteListParams = {}): UseQueryResult<SiteListResponse> {
  const api = useApi();
  const query = {
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
    search: params.search,
  };
  return useQuery({
    queryKey: key("/v1/sites", query),
    queryFn: ({ signal }) => api.request<SiteListResponse>("/v1/sites", { query, signal }),
    staleTime: 60_000,
  });
}

export function useSiteMeters(
  siteSlug: string | undefined,
  params: { kind?: string } = {},
): UseQueryResult<MeterListResponse> {
  const api = useApi();
  const path = siteSlug ? `/v1/sites/${encodeURIComponent(siteSlug)}/meters` : "";
  return useQuery({
    enabled: Boolean(siteSlug),
    queryKey: key(path, params),
    queryFn: ({ signal }) =>
      api.request<MeterListResponse>(path, { query: params, signal }),
    staleTime: 60_000,
  });
}

export type SiteDeviceListParams = {
  kind?: string;
  status?: string;
  limit?: number;
  offset?: number;
};

export function useSiteDevices(
  siteSlug: string | undefined,
  params: SiteDeviceListParams = {},
): UseQueryResult<SiteDeviceListResponse> {
  const api = useApi();
  const path = siteSlug ? `/v1/sites/${encodeURIComponent(siteSlug)}/devices` : "";
  return useQuery({
    enabled: Boolean(siteSlug),
    queryKey: key(path, params),
    queryFn: ({ signal }) =>
      api.request<SiteDeviceListResponse>(path, { query: params, signal }),
    staleTime: 60_000,
  });
}

export function useSiteDevice(
  siteSlug: string | undefined,
  deviceSlug: string | undefined,
): UseQueryResult<SiteDevice> {
  const api = useApi();
  const path =
    siteSlug && deviceSlug
      ? `/v1/sites/${encodeURIComponent(siteSlug)}/devices/${encodeURIComponent(deviceSlug)}`
      : "";
  return useQuery({
    enabled: Boolean(siteSlug && deviceSlug),
    queryKey: key(path),
    queryFn: ({ signal }) => api.request<SiteDevice>(path, { signal }),
    staleTime: 60_000,
  });
}

export type ReadingsParams = {
  from?: string;
  to?: string;
  limit?: number;
};

export function useMeterReadings(
  siteSlug: string | undefined,
  meterSlug: string | undefined,
  params: ReadingsParams = {},
): UseQueryResult<ReadingListResponse> {
  const api = useApi();
  const path =
    siteSlug && meterSlug
      ? `/v1/sites/${encodeURIComponent(siteSlug)}/meters/${encodeURIComponent(meterSlug)}/readings`
      : "";
  return useQuery({
    enabled: Boolean(siteSlug && meterSlug),
    queryKey: key(path, params),
    queryFn: ({ signal }) =>
      api.request<ReadingListResponse>(path, { query: params, signal }),
    staleTime: 30_000,
  });
}

export type AggregatesParams = {
  interval: AggregateInterval;
  from?: string;
  to?: string;
};

export function useMeterAggregates(
  siteSlug: string | undefined,
  meterSlug: string | undefined,
  params: AggregatesParams,
): UseQueryResult<AggregateResponse> {
  const api = useApi();
  const path =
    siteSlug && meterSlug
      ? `/v1/sites/${encodeURIComponent(siteSlug)}/meters/${encodeURIComponent(meterSlug)}/aggregates`
      : "";
  return useQuery({
    enabled: Boolean(siteSlug && meterSlug),
    queryKey: key(path, params),
    queryFn: ({ signal }) =>
      api.request<AggregateResponse>(path, { query: params, signal }),
    // Aggregates change less frequently than raw readings; 2 minutes
    // keeps the sparkline feeling live without hammering the API.
    staleTime: 2 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// /v1/alarms — viewer list + operator acknowledge (§9.5).
// ---------------------------------------------------------------------------

const ALARMS_PATH = "/v1/alarms";

export type AlarmsParams = {
  state?: AlarmState;
  severity?: AlarmSeverity;
  site_slug?: string;
  device_slug?: string;
  meter_slug?: string;
  limit?: number;
  offset?: number;
};

export function useAlarms(params: AlarmsParams = {}): UseQueryResult<AlarmListResponse> {
  const api = useApi();
  const query = {
    state: params.state,
    severity: params.severity,
    site_slug: params.site_slug,
    device_slug: params.device_slug,
    meter_slug: params.meter_slug,
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
  };
  return useQuery({
    queryKey: key(ALARMS_PATH, query),
    queryFn: ({ signal }) =>
      api.request<AlarmListResponse>(ALARMS_PATH, {
        query,
        signal,
      }),
    staleTime: 30_000,
  });
}

export function useOpenAlarmsSummary(): UseQueryResult<AlarmListResponse> {
  const api = useApi();
  const query = { state: "open", limit: 1, offset: 0 } as const;
  return useQuery({
    queryKey: key(ALARMS_PATH, query),
    queryFn: ({ signal }) =>
      api.request<AlarmListResponse>(ALARMS_PATH, {
        query,
        signal,
      }),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// ---------------------------------------------------------------------------
// /v1/admin/alarm-rules — admin-owned alarm tuning drafts.
// ---------------------------------------------------------------------------

const ADMIN_ALARM_RULES_PATH = "/v1/admin/alarm-rules";

export type AdminAlarmRulesParams = {
  status?: AlarmRuleStatus;
  severity?: AlarmSeverity;
  search?: string;
  limit?: number;
  offset?: number;
};

export function useAdminAlarmRules(
  params: AdminAlarmRulesParams = {},
): UseQueryResult<AdminAlarmRuleListResponse> {
  const api = useApi();
  const query = {
    status: params.status,
    severity: params.severity,
    search: params.search,
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
  };
  return useQuery({
    queryKey: key(ADMIN_ALARM_RULES_PATH, query),
    queryFn: ({ signal }) =>
      api.request<AdminAlarmRuleListResponse>(ADMIN_ALARM_RULES_PATH, {
        query,
        signal,
      }),
    staleTime: 30_000,
  });
}

export function useAdminAlarmRuleExport(
  enabled = false,
): UseQueryResult<AdminAlarmRuleExportResponse> {
  const api = useApi();
  return useQuery({
    enabled,
    queryKey: key(`${ADMIN_ALARM_RULES_PATH}/export`),
    queryFn: ({ signal }) =>
      api.request<AdminAlarmRuleExportResponse>(`${ADMIN_ALARM_RULES_PATH}/export`, {
        signal,
      }),
    staleTime: 30_000,
  });
}

function useInvalidateAdminAlarmRuleCaches(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) &&
        typeof q.queryKey[0] === "string" &&
        q.queryKey[0].startsWith(ADMIN_ALARM_RULES_PATH),
    });
  };
}

export function useCreateAdminAlarmRule(): UseMutationResult<
  AdminAlarmRule,
  Error,
  AdminAlarmRuleCreateBody
> {
  const api = useApi();
  const invalidate = useInvalidateAdminAlarmRuleCaches();
  return useMutation({
    mutationFn: (body) =>
      api.request<AdminAlarmRule>(ADMIN_ALARM_RULES_PATH, {
        method: "POST",
        body,
      }),
    onSuccess: () => invalidate(),
  });
}

export type UpdateAdminAlarmRuleArgs = {
  id: string;
  body: AdminAlarmRuleUpdateBody;
};

export function useUpdateAdminAlarmRule(): UseMutationResult<
  AdminAlarmRule,
  Error,
  UpdateAdminAlarmRuleArgs
> {
  const api = useApi();
  const invalidate = useInvalidateAdminAlarmRuleCaches();
  return useMutation({
    mutationFn: ({ id, body }) =>
      api.request<AdminAlarmRule>(`${ADMIN_ALARM_RULES_PATH}/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteAdminAlarmRule(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const invalidate = useInvalidateAdminAlarmRuleCaches();
  return useMutation({
    mutationFn: (id) =>
      api.request<void>(`${ADMIN_ALARM_RULES_PATH}/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    onSuccess: () => invalidate(),
  });
}

function useInvalidateAlarmCaches(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({
      predicate: (q) => {
        const k = q.queryKey;
        return Array.isArray(k) && typeof k[0] === "string" && k[0].startsWith(ALARMS_PATH);
      },
    });
  };
}

export function useAckAlarm(): UseMutationResult<Alarm, Error, string> {
  const api = useApi();
  const invalidate = useInvalidateAlarmCaches();
  return useMutation({
    mutationFn: (id) =>
      api.request<Alarm>(`${ALARMS_PATH}/${encodeURIComponent(id)}/ack`, {
        method: "POST",
      }),
    onSuccess: () => invalidate(),
  });
}

// ---------------------------------------------------------------------------
// /v1/admin/meters — CRUD hooks for the admin meters page (§9.4).
// ---------------------------------------------------------------------------

const ADMIN_METERS_PATH = "/v1/admin/meters";

export type AdminMetersParams = {
  site_slug?: string;
  device_slug?: string;
  kind?: string;
  status?: string;
  limit?: number;
  offset?: number;
};

/** Listing query. Filter args are forwarded as query params unchanged. */
export function useAdminMeters(
  params: AdminMetersParams = {},
): UseQueryResult<AdminMeterListResponse> {
  const api = useApi();
  const query = {
    site_slug: params.site_slug,
    device_slug: params.device_slug,
    kind: params.kind,
    status: params.status,
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
  };
  return useQuery({
    queryKey: key(ADMIN_METERS_PATH, query),
    queryFn: ({ signal }) =>
      api.request<AdminMeterListResponse>(ADMIN_METERS_PATH, {
        query,
        signal,
      }),
    // Admin lists don't need to be ultra-fresh; 30s avoids
    // a refetch storm while the operator clicks around the table.
    staleTime: 30_000,
  });
}

/**
 * Invalidate every cache entry that could embed the meter we just mutated.
 * That's:
 *   - the admin-meters list itself (any filter/page combination),
 *   - the viewer-side site-meters list and device detail (which carry
 *     `display_name`/`kind`/`unit`/`status` for the same row).
 */
function useInvalidateMeterCaches(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === ADMIN_METERS_PATH,
      }),
      queryClient.invalidateQueries({
        predicate: (q) => {
          const k = q.queryKey;
          if (!Array.isArray(k) || typeof k[0] !== "string") return false;
          return k[0].startsWith("/v1/sites/");
        },
      }),
    ]);
  };
}

export function useCreateMeter(): UseMutationResult<
  AdminMeter,
  Error,
  AdminMeterCreateBody
> {
  const api = useApi();
  const invalidate = useInvalidateMeterCaches();
  return useMutation({
    mutationFn: (body) =>
      api.request<AdminMeter>(ADMIN_METERS_PATH, { method: "POST", body }),
    onSuccess: () => invalidate(),
  });
}

export type UpdateMeterArgs = { id: string; body: AdminMeterUpdateBody };

export function useUpdateMeter(): UseMutationResult<
  AdminMeter,
  Error,
  UpdateMeterArgs
> {
  const api = useApi();
  const invalidate = useInvalidateMeterCaches();
  return useMutation({
    mutationFn: ({ id, body }) =>
      api.request<AdminMeter>(`${ADMIN_METERS_PATH}/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteMeter(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const invalidate = useInvalidateMeterCaches();
  return useMutation({
    mutationFn: (id) =>
      api.request<void>(`${ADMIN_METERS_PATH}/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    onSuccess: () => invalidate(),
  });
}

// ---------------------------------------------------------------------------
// /v1/admin/devices — CRUD hooks for the admin devices page (§9.3).
// ---------------------------------------------------------------------------

const ADMIN_DEVICES_PATH = "/v1/admin/devices";

export type AdminDevicesParams = {
  site_slug?: string;
  kind?: string;
  limit?: number;
  offset?: number;
};

/** Listing query. Filter args are forwarded as query params unchanged. */
export function useAdminDevices(
  params: AdminDevicesParams = {},
): UseQueryResult<AdminDeviceListResponse> {
  const api = useApi();
  const query = {
    site_slug: params.site_slug,
    kind: params.kind,
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
  };
  return useQuery({
    queryKey: key(ADMIN_DEVICES_PATH, query),
    queryFn: ({ signal }) =>
      api.request<AdminDeviceListResponse>(ADMIN_DEVICES_PATH, {
        query,
        signal,
      }),
    // Match the admin-meters cadence — operators click around the table,
    // 30s avoids a refetch storm without the list feeling stale.
    staleTime: 30_000,
  });
}

/**
 * Invalidate every cache entry that could embed a device row we just
 * mutated. That's:
 *   - the admin-devices list itself (any filter/page combo),
 *   - viewer-side site-devices and device-detail (which carry
 *     display_name / kind / status / meter_count for the same row).
 */
function useInvalidateDeviceCaches(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === ADMIN_DEVICES_PATH,
      }),
      queryClient.invalidateQueries({
        predicate: (q) => {
          const k = q.queryKey;
          if (!Array.isArray(k) || typeof k[0] !== "string") return false;
          return k[0].startsWith("/v1/sites/");
        },
      }),
    ]);
  };
}

export function useCreateDevice(): UseMutationResult<
  AdminDevice,
  Error,
  AdminDeviceCreateBody
> {
  const api = useApi();
  const invalidate = useInvalidateDeviceCaches();
  return useMutation({
    mutationFn: (body) =>
      api.request<AdminDevice>(ADMIN_DEVICES_PATH, { method: "POST", body }),
    onSuccess: () => invalidate(),
  });
}

export type UpdateDeviceArgs = { id: string; body: AdminDeviceUpdateBody };

export function useUpdateDevice(): UseMutationResult<
  AdminDevice,
  Error,
  UpdateDeviceArgs
> {
  const api = useApi();
  const invalidate = useInvalidateDeviceCaches();
  return useMutation({
    mutationFn: ({ id, body }) =>
      api.request<AdminDevice>(`${ADMIN_DEVICES_PATH}/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteDevice(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const invalidate = useInvalidateDeviceCaches();
  return useMutation({
    mutationFn: (id) =>
      api.request<void>(`${ADMIN_DEVICES_PATH}/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    onSuccess: () => invalidate(),
  });
}

// ---------------------------------------------------------------------------
// /v1/admin/ev-dc-modbus-targets — admin CRUD hooks for EV DC Modbus
// connector target configuration.
// ---------------------------------------------------------------------------

const ADMIN_EV_DC_MODBUS_TARGETS_PATH = "/v1/admin/ev-dc-modbus-targets";

export type EvDcModbusTargetsParams = {
  site_slug?: string;
  device_slug?: string;
  profile?: EvDcModbusProfile;
  writes_enabled?: boolean;
  limit?: number;
  offset?: number;
};

export function useEvDcModbusTargets(
  params: EvDcModbusTargetsParams = {},
): UseQueryResult<EvDcModbusTargetListResponse> {
  const api = useApi();
  const query = {
    site_slug: params.site_slug,
    device_slug: params.device_slug,
    profile: params.profile,
    writes_enabled: params.writes_enabled,
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
  };
  return useQuery({
    queryKey: key(ADMIN_EV_DC_MODBUS_TARGETS_PATH, query),
    queryFn: ({ signal }) =>
      api.request<EvDcModbusTargetListResponse>(ADMIN_EV_DC_MODBUS_TARGETS_PATH, {
        query,
        signal,
      }),
    staleTime: 30_000,
  });
}

export type EvDcModbusTargetExportParams = EvDcModbusTargetsParams & {
  include_write_flags?: boolean;
};

export function useEvDcModbusTargetExport(
  params: EvDcModbusTargetExportParams = {},
  enabled = false,
): UseQueryResult<EvDcModbusTargetPollerExportResponse> {
  const api = useApi();
  const query = {
    site_slug: params.site_slug,
    device_slug: params.device_slug,
    profile: params.profile,
    writes_enabled: params.writes_enabled,
    include_write_flags: params.include_write_flags ?? false,
    limit: params.limit ?? 200,
    offset: params.offset ?? 0,
  };
  return useQuery({
    enabled,
    queryKey: key(`${ADMIN_EV_DC_MODBUS_TARGETS_PATH}/export`, query),
    queryFn: ({ signal }) =>
      api.request<EvDcModbusTargetPollerExportResponse>(
        `${ADMIN_EV_DC_MODBUS_TARGETS_PATH}/export`,
        {
          query,
          signal,
        },
      ),
    staleTime: 30_000,
  });
}

function useInvalidateEvDcModbusTargetCaches(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) &&
        typeof q.queryKey[0] === "string" &&
        q.queryKey[0].startsWith(ADMIN_EV_DC_MODBUS_TARGETS_PATH),
    });
  };
}

export function useCreateEvDcModbusTarget(): UseMutationResult<
  EvDcModbusTarget,
  Error,
  EvDcModbusTargetCreateBody
> {
  const api = useApi();
  const invalidate = useInvalidateEvDcModbusTargetCaches();
  return useMutation({
    mutationFn: (body) =>
      api.request<EvDcModbusTarget>(ADMIN_EV_DC_MODBUS_TARGETS_PATH, {
        method: "POST",
        body,
      }),
    onSuccess: () => invalidate(),
  });
}

export type UpdateEvDcModbusTargetArgs = {
  id: string;
  body: EvDcModbusTargetUpdateBody;
};

export function useUpdateEvDcModbusTarget(): UseMutationResult<
  EvDcModbusTarget,
  Error,
  UpdateEvDcModbusTargetArgs
> {
  const api = useApi();
  const invalidate = useInvalidateEvDcModbusTargetCaches();
  return useMutation({
    mutationFn: ({ id, body }) =>
      api.request<EvDcModbusTarget>(
        `${ADMIN_EV_DC_MODBUS_TARGETS_PATH}/${encodeURIComponent(id)}`,
        { method: "PATCH", body },
      ),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteEvDcModbusTarget(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const invalidate = useInvalidateEvDcModbusTargetCaches();
  return useMutation({
    mutationFn: (id) =>
      api.request<void>(
        `${ADMIN_EV_DC_MODBUS_TARGETS_PATH}/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => invalidate(),
  });
}

// ---------------------------------------------------------------------------
// /v1/admin/notification-settings — admin-managed delivery settings. Secrets
// are write-only; reads only expose configured/not-configured flags.
// ---------------------------------------------------------------------------

const ADMIN_NOTIFICATION_SETTINGS_PATH = "/v1/admin/notification-settings";

export function useAdminNotificationSettings(): UseQueryResult<AdminNotificationSettings> {
  const api = useApi();
  return useQuery({
    queryKey: key(ADMIN_NOTIFICATION_SETTINGS_PATH),
    queryFn: ({ signal }) =>
      api.request<AdminNotificationSettings>(ADMIN_NOTIFICATION_SETTINGS_PATH, {
        signal,
      }),
    staleTime: 30_000,
  });
}

export function useUpdateAdminNotificationSettings(): UseMutationResult<
  AdminNotificationSettings,
  Error,
  AdminNotificationSettingsUpdateBody
> {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.request<AdminNotificationSettings>(ADMIN_NOTIFICATION_SETTINGS_PATH, {
        method: "PATCH",
        body,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: key(ADMIN_NOTIFICATION_SETTINGS_PATH),
      }),
  });
}

export function useTestAdminNotificationSettings(): UseMutationResult<
  AdminNotificationTestResult,
  Error,
  void
> {
  const api = useApi();
  return useMutation({
    mutationFn: () =>
      api.request<AdminNotificationTestResult>(`${ADMIN_NOTIFICATION_SETTINGS_PATH}/test`, {
        method: "POST",
      }),
  });
}

// ---------------------------------------------------------------------------
// /v1/ev-charger-commands — viewer list + operator create + operator
// transition (§18.1 EV charger campaign).
//
// Cancel-from-pending is the only transition the SPA exposes today; the
// dispatched/succeeded/failed states are owned by the poller-side executor
// (see PR #40). Keeping the hook generic over `state` means the same
// mutation can later carry "force fail" or other operator overrides
// without a second hook.
// ---------------------------------------------------------------------------

const EV_CHARGER_COMMANDS_PATH = "/v1/ev-charger-commands";

export type EvChargerCommandsParams = {
  state?: EvChargerCommandState;
  site_slug?: string;
  device_slug?: string;
  limit?: number;
  offset?: number;
};

export function useEvChargerCommands(
  params: EvChargerCommandsParams = {},
): UseQueryResult<EvChargerCommandListResponse> {
  const api = useApi();
  const query = {
    state: params.state,
    site_slug: params.site_slug,
    device_slug: params.device_slug,
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
  };
  return useQuery({
    queryKey: key(EV_CHARGER_COMMANDS_PATH, query),
    queryFn: ({ signal }) =>
      api.request<EvChargerCommandListResponse>(EV_CHARGER_COMMANDS_PATH, {
        query,
        signal,
      }),
    // Audit rows mutate at human cadence; keep refresh cheap but not stale.
    staleTime: 30_000,
  });
}

function useInvalidateEvChargerCommandCaches(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({
      predicate: (q) => {
        const k = q.queryKey;
        return (
          Array.isArray(k) &&
          typeof k[0] === "string" &&
          k[0].startsWith(EV_CHARGER_COMMANDS_PATH)
        );
      },
    });
  };
}

export function useCreateEvChargerCommand(): UseMutationResult<
  EvChargerCommand,
  Error,
  EvChargerCommandCreateBody
> {
  const api = useApi();
  const invalidate = useInvalidateEvChargerCommandCaches();
  return useMutation({
    mutationFn: (body) =>
      api.request<EvChargerCommand>(EV_CHARGER_COMMANDS_PATH, {
        method: "POST",
        body,
      }),
    onSuccess: () => invalidate(),
  });
}

export type TransitionEvChargerCommandArgs = {
  id: string;
  body: EvChargerCommandTransitionBody;
};

export function useTransitionEvChargerCommand(): UseMutationResult<
  EvChargerCommand,
  Error,
  TransitionEvChargerCommandArgs
> {
  const api = useApi();
  const invalidate = useInvalidateEvChargerCommandCaches();
  return useMutation({
    mutationFn: ({ id, body }) =>
      api.request<EvChargerCommand>(
        `${EV_CHARGER_COMMANDS_PATH}/${encodeURIComponent(id)}/transition`,
        { method: "POST", body },
      ),
    onSuccess: () => invalidate(),
  });
}
